import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, Search, Check } from 'lucide-react';
import { getSurveyIconComponent } from '../Survey/employee/surveyIcons';

const cn = (...classes) => classes.filter(Boolean).join(' ');

const Select = ({
  options = [],
  value = null,
  onChange,
  placeholder = 'Select options...',
  searchPlaceholder = 'Search options...',
  multiple = false,
  searchable = false,
  clearable = false,
  disabled = false,
  className = '',
  size = 'default',
  maxHeight = '200px',
  closeOnSelect = true,
  dropdownMenuClassName = '',
  onOpenChange,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedValues, setSelectedValues] = useState(() =>
    multiple ? (Array.isArray(value) ? value : value ? [value] : []) : value && value !== '' ? [value] : [],
  );
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setSelectedValues(
      multiple ? (Array.isArray(value) ? value : value ? [value] : []) : value && value !== '' ? [value] : [],
    );
  }, [value, multiple]);

  const filteredOptions = options.filter(
    (option) =>
      !searchQuery || (typeof option === 'string' ? option : option.label).toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleOptionSelect = (option) => {
    const optionValue = typeof option === 'string' ? option : option.value;

    if (multiple) {
      const newValues = selectedValues.includes(optionValue)
        ? selectedValues.filter((v) => v !== optionValue)
        : [...selectedValues, optionValue];
      setSelectedValues(newValues);
      onChange?.(newValues);
      setIsOpen(!closeOnSelect);
    } else {
      setSelectedValues([optionValue]);
      onChange?.(optionValue);
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedValues([]);
    onChange?.(multiple ? [] : null);
    setSearchQuery('');
  };

  const handleTagRemove = (valueToRemove, e) => {
    e.stopPropagation();
    const newValues = selectedValues.filter((v) => v !== valueToRemove);
    setSelectedValues(newValues);
    onChange?.(newValues);
  };

  const getOption = (val) => {
    if (val == null || val === '') return null;
    return options.find((opt) => (typeof opt === 'string' ? opt : opt.value) === val) || null;
  };

  const getOptionLabel = (val) => {
    if (!val) return '';
    const option = getOption(val);
    return option ? (typeof option === 'string' ? option : option.label) : val;
  };

  const getOptionIcon = (val) => {
    const option = getOption(val);
    if (!option || typeof option === 'string') return null;
    return getSurveyIconComponent(option.icon);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchable && searchRef.current) searchRef.current.focus();
  }, [isOpen, searchable]);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  const hasValue = selectedValues.length > 0;
  const displayValue = multiple
    ? selectedValues.map(getOptionLabel).join(', ')
    : selectedValues.length > 0
      ? getOptionLabel(selectedValues[0])
      : '';

  const visibleTagsCount = isMobile ? 0 : 3;
  const maxTagLength = isMobile ? 0 : 20;
  const isXs = size === 'xs';
  const triggerIcon = isXs ? 14 : 16;

  return (
    <div ref={containerRef} className={cn('relative w-full', className)} {...props}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={cn(
          'w-full flex items-center justify-between gap-2 rounded-lg border-[1.5px] bg-transparent shadow-sm transition-all duration-200 cursor-pointer',
          'border-input hover:border-primary focus-visible:outline-none focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-primary/15',
          isXs && 'h-7 rounded-md px-2 py-0 text-xs gap-1.5',
          size === 'sm' && 'h-8 px-3 py-2 text-sm gap-2',
          size === 'default' && 'h-9 px-3 py-2 text-sm gap-2',
          disabled && 'cursor-not-allowed opacity-50',
          isOpen && 'border-primary ring-[3px] ring-primary/15',
        )}>
        <div className='flex-1 flex items-center gap-1 min-w-0 overflow-hidden h-full'>
          {multiple && hasValue ? (
            <div className='flex items-center gap-1 max-w-full overflow-hidden'>
              {selectedValues.slice(0, visibleTagsCount).map((val) => (
                <span
                  key={val}
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-secondary text-secondary-foreground rounded-lg flex-shrink-0',
                    isMobile && 'max-w-20',
                  )}>
                  <span className='truncate'>
                    {getOptionLabel(val).length <= maxTagLength
                      ? getOptionLabel(val)
                      : getOptionLabel(val).substring(0, maxTagLength) + '...'}
                  </span>
                  <button
                    onClick={(e) => handleTagRemove(val, e)}
                    className='ml-1 hover:bg-secondary-foreground/20 rounded-sm p-0.5 flex-shrink-0'>
                    <X size={12} strokeWidth={1.5} />
                  </button>
                </span>
              ))}
              {selectedValues.length > visibleTagsCount && (
                <span
                  className={cn(
                    'text-xs font-semibold text-muted-foreground flex-shrink-0',
                    isMobile ? 'bg-muted px-2 py-0.5 rounded-md' : '',
                  )}>
                  +{selectedValues.length - visibleTagsCount} more
                </span>
              )}
            </div>
          ) : (
            <span className={cn('truncate flex-1 text-left flex items-center gap-2 min-w-0', !hasValue && 'text-muted-foreground')}>
              {hasValue ? (
                <>
                  {(() => {
                    const Icon = !multiple && selectedValues[0] != null ? getOptionIcon(selectedValues[0]) : null;
                    return Icon ? (
                      <span className='w-7 h-7 rounded-md bg-primary flex items-center justify-center text-primary-foreground shrink-0'>
                        <Icon size={14} strokeWidth={1.5} />
                      </span>
                    ) : null;
                  })()}
                  <span className='truncate'>{displayValue}</span>
                </>
              ) : (
                placeholder
              )}
            </span>
          )}
        </div>

        <div className='flex items-center gap-1'>
          {clearable && hasValue && (
            <button
              type='button'
              onClick={handleClear}
              className={cn('hover:text-destructive duration-300 rounded-sm shrink-0', isXs ? 'p-0.5' : 'p-1')}>
              <X size={triggerIcon} strokeWidth={1.5} />
            </button>
          )}
          <ChevronDown size={triggerIcon} strokeWidth={1.5} className={cn('shrink-0 transition-transform', isOpen && 'rotate-180')} />
        </div>
      </div>

      {isOpen && (
        <div
          className={cn(
            'absolute top-full left-0 mt-1 bg-popover border rounded-md shadow-lg overflow-hidden',
            dropdownMenuClassName || 'right-0 z-[100] w-full max-w-full',
            isMobile && 'max-h-[50vh]',
          )}>
          {searchable && (
            <div className={cn('flex items-center gap-2 border-b', isXs ? 'p-1.5' : 'p-2')}>
              <Search size={triggerIcon} strokeWidth={1.5} className='text-muted-foreground shrink-0' />
              <input
                ref={searchRef}
                type='text'
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn('flex-1 bg-transparent outline-none min-w-0', isXs ? 'text-xs' : 'text-sm')}
              />
            </div>
          )}

          <div
            className={cn('overflow-y-auto px-2 my-2', isMobile && 'max-h-40', !isMobile && 'max-h-60')}
            style={{ maxHeight: isMobile ? '160px' : maxHeight }}>
            {filteredOptions.length === 0 ? (
              <div className={cn('px-2 py-3 text-muted-foreground text-center', isXs ? 'text-xs' : 'text-sm')}>
                No options found
              </div>
            ) : (
              filteredOptions.map((option) => {
                if (option === undefined || option === null) return;
                const optionValue = typeof option === 'string' ? option : option.value;
                const optionLabel = typeof option === 'string' ? option : option.label;
                const OptionIcon = typeof option === 'string' ? null : getSurveyIconComponent(option.icon);
                const isSelected = selectedValues.includes(optionValue);

                return (
                  <div
                    key={optionValue}
                    onClick={() => handleOptionSelect(option)}
                    className={cn(
                      'flex items-center gap-2 rounded-sm cursor-pointer transition-colors',
                      'hover:bg-accent hover:text-accent-foreground',
                      isXs ? 'px-2 py-1 text-xs' : 'px-2 py-1.5 text-sm',
                      isMobile && 'py-2',
                    )}>
                    {multiple && (
                      <div
                        className={cn(
                          'w-4 h-4 border rounded-sm flex items-center justify-center flex-shrink-0',
                          isSelected && 'bg-primary border-primary',
                        )}>
                        {isSelected && <Check size={12} strokeWidth={3} className='text-primary-foreground' />}
                      </div>
                    )}
                    {OptionIcon ? (
                      <span className='w-7 h-7 rounded-md bg-muted flex items-center justify-center text-muted-foreground shrink-0'>
                        <OptionIcon size={14} strokeWidth={1.5} />
                      </span>
                    ) : null}
                    <span className='flex-1 min-w-0 break-words'>{optionLabel}</span>
                    {!multiple && isSelected && (
                      <Check size={isXs ? 12 : 14} strokeWidth={2} className='flex-shrink-0' />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export { Select };
export { Select as newSelect };
